// Access Control List (ACL) and Permission Matrix for Scrum Poker Application
// Structured around RBAC core elements: Roles, Resources, Actions
/**
 * RBAC Permission Matrix
 * Defines what actions each role can perform on each resource
 */
export const PERMISSION_MATRIX = {
    participant: {
        "room:create": true,
        "room:read": true,
        "room:update": false,
        "room:delete": true,
        "room:join": true,
        "room:leave": true,
        "vote:cast": true,
        "vote:read": true,
        "round:reveal": true,
        "round:clear": true,
        "round:read": true,
        "participant:read": true,
        "session:control": true,
    },
    visitor: {
        "room:create": true,
        "room:read": true,
        "room:update": false,
        "room:delete": false,
        "room:join": true,
        "room:leave": true,
        "vote:cast": false,
        "vote:read": true,
        "round:reveal": false,
        "round:clear": false,
        "round:read": true,
        "participant:read": true,
        "session:control": false,
    },
    facilitator: {
        "room:create": true,
        "room:read": true,
        "room:update": false,
        "room:delete": true,
        "room:join": true,
        "room:leave": true,
        "vote:cast": false,
        "vote:read": true,
        "round:reveal": true,
        "round:clear": true,
        "round:read": true,
        "participant:read": true,
        "session:control": true,
    },
};
// ===== CORE ACL BUSINESS LOGIC =====
/**
 * Core permission check function
 */
export function hasPermission(role, permission) {
    return PERMISSION_MATRIX[role]?.[permission] ?? false;
}
/**
 * Advanced permission check with context validation
 */
export function canPerformAction(permission, context) {
    const { userRole } = context;
    // Base permission check
    if (!hasPermission(userRole, permission)) {
        return false;
    }
    return true;
}
/**
 * Permission validation errors
 */
export class PermissionError extends Error {
    permission;
    userRole;
    constructor(permission, userRole, message) {
        super(message || `Permission '${permission}' denied for role '${userRole}'`);
        this.permission = permission;
        this.userRole = userRole;
        this.name = "PermissionError";
    }
}
/**
 * Validate permission and throw error if not allowed
 */
export function requirePermission(permission, context) {
    if (!canPerformAction(permission, context)) {
        throw new PermissionError(permission, context.userRole, `Action '${permission}' not allowed for role '${context.userRole}'`);
    }
}
/**
 * Get all permissions for a role
 */
export function getRolePermissions(role) {
    const permissions = PERMISSION_MATRIX[role];
    if (!permissions)
        return [];
    return Object.entries(permissions)
        .filter(([_, allowed]) => allowed)
        .map(([permission, _]) => permission);
}
/**
 * Role hierarchy for privilege escalation checks
 */
export const ROLE_HIERARCHY = {
    visitor: 1,
    facilitator: 2,
    participant: 2,
};
/**
 * Check if one role has higher privileges than another
 */
export function hasHigherPrivilege(role1, role2) {
    return ROLE_HIERARCHY[role1] > ROLE_HIERARCHY[role2];
}
// ===== BUSINESS LOGIC HELPERS =====
/**
 * Business logic: Check if user can vote
 */
export function canVote(role) {
    return hasPermission(role, "vote:cast");
}
/**
 * Business logic: Check if user can control session (reveal/clear votes)
 */
export function canControlSession(role) {
    return hasPermission(role, "session:control");
}
/**
 * Business logic: Check if user can view voting results
 */
export function canViewResults(role) {
    return hasPermission(role, "round:read");
}
